import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Controller, useForm, useWatch, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { mutate } from '@/runtime'
import {
  useEdgesFrom,
  useEdgesTo,
  useMeta,
  useProjects,
  useSettings,
  useTasks,
  usePersonLinks,
} from '@/db/hooks'
import { PagesSection } from '@/kinds/page/components'
import { ListsSection } from '@/kinds/list/components'
import { BecameBanner, ConvertSheet } from '@/components/ConvertSheet'
import { useAllLists, useAllPages, useAllProjects, useListsFor, usePagesFor } from '@/db/hooks'
import { RemindPicker } from '@/components/RemindPicker'
import { quickAddParse } from '@/kinds/task/parse'
import { matchProject } from '@/components/QuickAdd'
import { Icon } from '@/lib/icons'
import { META } from '@/db/schema'
import { db } from '@/runtime'
import type { Resource } from '@/db/types'
import { taskActions } from '@/lib/actions'
import { isJob } from '@/lib/jobs'
import { dropCapture } from '@/lib/captures'
import { addDays, fmtDateInPhrase, fmtElapsed, fmtMinutes, todayLocal } from '@/lib/time'
import { useUi } from '@/state/ui'
import { DeferMenu } from '@/components/DeferMenu'
import { ProjectPicker } from '@/components/ProjectPicker'
import { PeoplePicker } from '@/components/PersonBits'
import { ClashNote } from '@/components/DateBits'
import { TaskMeta, TaskRow } from '@/components/TaskRow'
import { Button, Field, IconButton, Sheet, Tag, cx } from '@/components/ui'
import { RepeatPicker } from '@/components/RepeatPicker'
import { IconButtonField } from '@/components/EmojiPicker'
import { KindError } from '@/kinds/kind'
import { HandToAgentSheet, TaskRuns } from '@/kinds/agentrun/components'
import { useAgentsHere } from '@/hooks/useCodeWork'
import { Phrases } from '@/components/Phrases'
import { describeRule } from '@/components/RepeatPicker'
import { isStale, jobBaseline } from '@/lib/jobs'
import { useNow } from '@/hooks/useNow'
import { KeepPosted } from '@/components/KeepPosted'
import { RingsToo } from '@/components/RingsToo'
import { ActionBar, type ActionItem } from '@/components/ActionBar'
import { useNudgeItem } from '@/components/NudgeButton'
import { useNudgeMembersItem } from '@/components/NudgeMembersButton'
import { useShareWithItem } from '@/components/ShareWith'
import { hiddenLabel } from '@/lib/follow'
import { useAccess } from '@/state/access'
import { LabelsEditor } from '@/components/LabelsEditor'
import { PRIORITY_WORD, TASK_STATE_WORD } from '@/lib/words'
import { isOpen, isTemplate, taskSpec, type TaskSpec, type TaskStatus } from './schema'
import type { TaskResource } from './apply'

type Props = { resource: Resource<TaskSpec, TaskStatus> }

export const TaskListRow = ({ resource }: Props) => (
  <ul className="group-list">
    <TaskRow task={resource} />
  </ul>
)

/** "Big one. Break it down?" — non-blocking, dismissable per task on this device. */
const SplitNudge = ({ task, projectId }: { task: TaskResource; projectId?: string }) => {
  const settings = useSettings()
  const dismissed = useMeta<Record<string, boolean>>(META.splitDismissed) ?? {}
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const est = task.spec.estimate_minutes ?? 0
  if (
    !isOpen(task.status.state) ||
    (task.status.steps_total ?? 0) > 0 ||
    est <= settings.split_threshold ||
    (task.spec.checklist?.length ?? 0) > 0 ||
    dismissed[task.id]
  )
    return null
  const addStep = async () => {
    const t = text.trim()
    if (!t) return
    await mutate.patch(task.id, {
      spec: { checklist: [...(task.spec.checklist ?? []), { text: t, done: false }] },
    })
    setText('')
  }
  const toSubtasks = async () => {
    const lines = text
      .split(/[\n,;]/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (!lines.length) return
    setBusy(true)
    for (const line of lines) {
      // a step belongs to the parent's day and due date, as Prime's steps do
      const sub = await mutate.create(
        'Task',
        {
          title: line,
          ...(task.spec.scheduled ? { scheduled: task.spec.scheduled } : {}),
          ...(task.spec.due ? { due: task.spec.due } : {}),
        },
        { ...task.labels },
      )
      await mutate.setEdge(sub.id, task.id, 'references')
      if (projectId) await mutate.setProject(sub.id, projectId)
    }
    setBusy(false)
    setText('')
  }
  const dismiss = () => void db.setMeta(META.splitDismissed, { ...dismissed, [task.id]: true })
  return (
    <div className="rounded-[var(--radius-card)] bg-accent-soft p-3 mb-4 fade-in" data-testid="split-nudge">
      <div className="flex justify-between items-start gap-2">
        <p className="text-sm text-fg">Big one. Break it down?</p>
        <button type="button" className="text-xs text-muted min-h-8 px-2" onClick={dismiss}>
          Not now
        </button>
      </div>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="First small step…"
        className="w-full my-2"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void addStep()
          }
        }}
      />
      <div className="flex gap-2">
        <Button variant="primary" onClick={() => void addStep()} disabled={!text.trim()}>
          Add as step
        </Button>
        <Button onClick={() => void toSubtasks()} disabled={!text.trim() || busy}>
          Make sub-tasks
        </Button>
      </div>
    </div>
  )
}

/** On a recurring template: the open copy to tick (meds, watering, the bins), or when the next one lands. */
const TodaysCopy = ({
  template,
  tasks,
  today,
}: {
  template: TaskResource
  tasks: TaskResource[]
  today: string
}) => {
  const copy = tasks.find(
    (t) => t.status.template_id === template.id && !t.deleted_at && isOpen(t.status.state),
  )
  const doneToday = tasks.find(
    (t) =>
      t.status.template_id === template.id &&
      !t.deleted_at &&
      t.status.state === 'done' &&
      (t.status.completed_at ?? '').slice(0, 10) === today,
  )
  return (
    <div className="card p-3 mb-4 !bg-raised flex items-center gap-3" data-testid="todays-copy">
      {copy ? (
        <>
          <button
            type="button"
            onClick={() => void taskActions.complete(copy)}
            className="h-7 w-7 rounded-full border-2 border-accent flex items-center justify-center hover:bg-accent/20 shrink-0"
            aria-label="Done for today"
            data-testid="todays-copy-complete"
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {copy.spec.scheduled === today
                ? 'Due today'
                : `Next copy ${fmtDateInPhrase(copy.spec.scheduled ?? '', today)}`}
            </div>
            <div className="text-xs text-muted">
              Tick it here or on Today. Each copy is its own task; missed days never pile up.
            </div>
          </div>
          <Link
            to="/tasks/$id"
            params={{ id: copy.id }}
            className="text-xs text-faint hover:text-muted shrink-0"
          >
            Open
          </Link>
        </>
      ) : doneToday ? (
        <>
          <span className="h-7 w-7 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
            ✓
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium">Done for today</div>
            <div className="text-xs text-muted">
              Next copy {template.status.next_due ? fmtDateInPhrase(template.status.next_due, today) : 'soon'}
              .
            </div>
          </div>
          <Button variant="chip" onClick={() => void taskActions.reopen(doneToday)}>
            Undo
          </Button>
        </>
      ) : (
        <div className="text-sm text-muted">
          Next copy {template.status.next_due ? fmtDateInPhrase(template.status.next_due, today) : 'soon'}.
        </div>
      )}
    </div>
  )
}

const everyWords = (n: number) =>
  n === 1
    ? 'Every day'
    : n === 7
      ? 'Every week'
      : n === 14
        ? 'Every fortnight'
        : n === 30
          ? 'Every month'
          : `Every ${n} days`

/**
 * What kind of task this is, in a line under the chips: a recurring one's
 * rule and nag, a house job's round, its last time and when it is due.
 */
const KindLine = ({ task, today }: { task: TaskResource; today: string }) => {
  if (isTemplate(task.spec))
    return (
      <Phrases
        as="p"
        className="text-[13px] text-muted mt-2"
        data-testid="task-kind-line"
        parts={[
          describeRule(task.spec.recurrence),
          task.spec.nag_minutes ? `nags every ${fmtMinutes(task.spec.nag_minutes)} until done` : '',
        ]}
      />
    )
  if (!isJob(task)) return null
  const due = addDays(jobBaseline(task), task.spec.every_days!)
  return (
    <Phrases
      as="p"
      className="text-[13px] text-muted mt-2"
      data-testid="task-kind-line"
      parts={[
        `${everyWords(task.spec.every_days!)} or so`,
        task.status.done_at ? `last done ${fmtDateInPhrase(task.status.done_at, today)}` : 'not done yet',
        isStale(task, today) ? 'due now' : `due ${fmtDateInPhrase(due, today)}`,
      ]}
    />
  )
}

export const TaskDetail = ({ resource: task }: Props) => {
  const nudgeItem = useNudgeItem(task.id)
  const nudgeMembersItem = useNudgeMembersItem(task.id, task.labels.ws)
  const shareWithItem = useShareWithItem({
    kind: 'Task',
    id: task.id,
    title: task.spec.title,
    ws: task.labels.ws,
  })
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [defer, setDefer] = useState(false)
  const [pickProject, setPickProject] = useState(false)
  const [pickPeople, setPickPeople] = useState(false)
  const withPeople = usePersonLinks().get(task.id) ?? []
  const [converting, setConverting] = useState(false)
  const [handOff, setHandOff] = useState(false)
  const allProjects = useAllProjects() ?? []
  const allPages = useAllPages() ?? []
  const allLists = useAllLists() ?? []
  const pagesHere = usePagesFor(task.id)
  const listsHere = useListsFor(task.id)
  const member = useAccess((s) => s.member)
  const becameTitle = (id: string) =>
    allProjects.find((p) => p.id === id)?.spec.title ??
    allPages.find((p) => p.id === id)?.name ??
    allLists.find((l) => l.id === id)?.spec.title ??
    tasks.find((t) => t.id === id)?.spec.title
  const edgesFrom = useEdgesFrom(task.id)
  const edgesTo = useEdgesTo(task.id)
  const projects = useProjects() ?? []
  const tasks = useTasks() ?? []
  const projectId = edgesFrom.find((e) => e.relation === 'belongs_to')?.to_id
  const project = projects.find((p) => p.id === projectId)
  // a house task is never handed to a coding agent: only code work offers it
  const factoryOn = useAgentsHere(task.labels.ws, project?.spec.repos)
  const subtasks = edgesTo
    .filter((e) => e.relation === 'references')
    .map((e) => tasks.find((t) => t.id === e.from_id))
    .filter((t): t is TaskResource => !!t && !t.deleted_at)
  const template = task.status.template_id ? tasks.find((t) => t.id === task.status.template_id) : undefined
  const running = !!task.status.started_at
  const now = useNow(running ? 1000 : undefined)
  const today = todayLocal()
  const open = isOpen(task.status.state)
  const flash = useUi((s) => s.showFlash)

  const toggleItem = (i: number) => {
    const list = (task.spec.checklist ?? []).map((c, k) => (k === i ? { ...c, done: !c.done } : c))
    void mutate.patch(task.id, { spec: { checklist: list } })
  }
  // no confirm: the delete is soft, the flash offers Undo and the Trash holds it
  const remove = async () => {
    await taskActions.remove(task)
    void navigate({ to: '/' })
  }
  const moveTo = (state: 'next' | 'waiting' | 'someday', label: string): ActionItem => ({
    id: state,
    label,
    hidden: !open || task.status.state === state,
    onSelect: () => void taskActions.move(task, state),
  })
  const actions: ActionItem[] = [
    {
      id: 'remind',
      label: 'Remind me',
      render: ({ as }) => (
        <RemindPicker
          as={as}
          value={task.spec.remind_at}
          fired={!!task.spec.remind_at && task.status.reminder_fired_for === task.spec.remind_at}
          onChange={(v) => void mutate.patch(task.id, { spec: { remind_at: v } })}
          note={<RingsToo taskId={task.id} ws={task.labels.ws} className="-mt-1 mb-3" />}
        />
      ),
    },
    {
      id: 'reschedule',
      label: 'Reschedule',
      pin: true,
      hidden: !open,
      onSelect: () => setDefer(true),
      testid: 'task-reschedule',
    },
    {
      id: 'reopen',
      label: 'Reopen',
      pin: true,
      hidden: open,
      onSelect: () => void taskActions.reopen(task),
    },
    {
      id: 'project',
      label: project ? 'Move project' : 'Add to project',
      hidden: !open,
      onSelect: () => setPickProject(true),
    },
    {
      id: 'people',
      label: withPeople.length
        ? `With ${withPeople.map((p) => p.spec.name.split(/\s+/)[0]).join(', ')}`
        : 'With someone',
      // People are the owner's: a member's app has none to pick from
      hidden: !open || member,
      onSelect: () => setPickPeople(true),
      testid: 'task-people',
    },
    moveTo('next', 'Next'),
    moveTo('waiting', 'Waiting'),
    moveTo('someday', 'Someday'),
    { ...nudgeItem, hidden: nudgeItem.hidden || !open },
    { ...nudgeMembersItem, hidden: nudgeMembersItem.hidden || !open },
    { ...shareWithItem, hidden: shareWithItem.hidden || !open },
    {
      id: 'convert',
      label: 'Convert to…',
      hidden: !open,
      onSelect: () => setConverting(true),
      testid: 'task-convert',
    },
    {
      id: 'hand-off',
      label: 'Hand to agent',
      hidden: !open || !factoryOn,
      onSelect: () => setHandOff(true),
      testid: 'task-hand-off',
    },
    { id: 'drop', label: 'Drop', hidden: !open, onSelect: () => void taskActions.drop(task) },
    { id: 'delete', label: 'Delete', tone: 'danger', onSelect: () => void remove(), testid: 'task-delete' },
    {
      id: 'drop-capture',
      label: 'Delete with all it made',
      tone: 'danger',
      hidden: !task.labels.via,
      onSelect: () => {
        void dropCapture(task.id, task.spec.title)
        void navigate({ to: '/' })
      },
      testid: 'task-drop-capture',
    },
  ]

  return (
    <article className="fade-in">
      <div className="flex items-start gap-2 mb-2">
        <h1 className={`text-xl font-semibold flex-1 break-words ${!open ? 'text-faint line-through' : ''}`}>
          <Icon value={task.spec.icon} size={20} className="mr-2" />
          {task.spec.title}
        </h1>
        <IconButton label="Edit" onClick={() => setEditing(true)}>
          ✎
        </IconButton>
      </div>
      <div className="mb-3">
        <TaskMeta task={task} project={project} />
        {/* one chip per state: the row's chips already say waiting and someday, a finished
            one says when, and a template's state is only bookkeeping */}
        <div className="flex flex-wrap gap-1 mt-1">
          {!isTemplate(task.spec) &&
            (task.status.state === 'inbox' ||
              task.status.state === 'next' ||
              (!open && !task.status.completed_at)) && <Tag>{TASK_STATE_WORD[task.status.state]}</Tag>}
          {!open && task.status.completed_at && (
            <Tag tone={task.status.state === 'done' ? 'done' : 'muted'}>
              {TASK_STATE_WORD[task.status.state]}{' '}
              {fmtDateInPhrase(task.status.completed_at.slice(0, 10), today)}
            </Tag>
          )}
          {task.status.reschedule_count > 0 && <Tag>moved {task.status.reschedule_count}×</Tag>}
          {template && (
            <Link to="/tasks/$id" params={{ id: template.id }}>
              <Tag tone="accent">from “{template.spec.title}”</Tag>
            </Link>
          )}
        </div>
        <KindLine task={task} today={today} />
      </div>

      {task.status.converted_to && <BecameBanner to={task.status.converted_to} titleOf={becameTitle} />}
      {isTemplate(task.spec) && open && <TodaysCopy template={task} tasks={tasks} today={today} />}

      {open && !isTemplate(task.spec) && (
        // the timer only when there is time to show (running, tracked or estimated);
        // a task with none of that is just its buttons, which then keep to one line
        <div className="card p-3 mb-4 flex flex-wrap items-center gap-3 !bg-raised">
          {(running || !!task.status.actual_minutes || !!task.spec.estimate_minutes) && (
            <div className="flex-1 min-w-[7rem]">
              <div className="text-2xl tabular-nums font-medium">
                {running
                  ? fmtElapsed(task.status.started_at!, now)
                  : task.status.actual_minutes
                    ? fmtMinutes(task.status.actual_minutes)
                    : fmtMinutes(task.spec.estimate_minutes!)}
              </div>
              <Phrases
                as="div"
                className="text-xs text-muted"
                parts={[
                  running ? 'elapsed' : task.status.actual_minutes ? 'tracked so far' : 'estimate',
                  (running || !!task.status.actual_minutes) &&
                    !!task.spec.estimate_minutes &&
                    `estimate ${fmtMinutes(task.spec.estimate_minutes)}`,
                ]}
              />
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <Button big className="whitespace-nowrap" onClick={() => void taskActions.toggleTimer(task)}>
              {running ? 'Stop' : 'Start'}
            </Button>
            {isJob(task) && (
              <Button
                big
                className="whitespace-nowrap"
                onClick={() => void taskActions.doneNow(task)}
                data-testid="done-now"
              >
                Done for now
              </Button>
            )}
            <Button
              big
              variant="primary"
              className="whitespace-nowrap"
              onClick={() => void taskActions.complete(task)}
              data-testid="complete"
            >
              Complete
            </Button>
          </div>
        </div>
      )}

      <SplitNudge task={task} projectId={projectId} />

      {task.spec.notes && <p className="text-sm text-fg-2 whitespace-pre-wrap mb-4">{task.spec.notes}</p>}
      {subtasks.length > 0 && (
        <section className="mb-4" data-testid="task-steps">
          <h2 className="text-[13px] font-semibold text-muted mb-2">
            <Phrases
              parts={[
                'Steps',
                !!task.status.steps_total && (
                  <span className="font-normal">
                    {task.status.steps_done ?? 0} of {task.status.steps_total} done
                  </span>
                ),
              ]}
            />
          </h2>
          <ul className="group-list">
            {subtasks.map((t) => (
              <TaskRow key={t.id} task={t} mode="compact" />
            ))}
          </ul>
        </section>
      )}

      {/* the checklist is the task's own ticks (no tasks of their own, unlike Steps): named, one surface */}
      {(task.spec.checklist?.length ?? 0) > 0 && (
        <section className="mb-4" data-testid="task-checklist">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Checklist</h2>
          <ul className="group-list">
            {task.spec.checklist!.map((c, i) => (
              <li key={i}>
                <label className="flex items-center gap-3 px-3.5 min-h-11 text-sm">
                  <input
                    type="checkbox"
                    checked={c.done}
                    onChange={() => toggleItem(i)}
                    className="h-5 w-5 shrink-0"
                  />
                  <span className={c.done ? 'line-through text-faint' : ''}>{c.text}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* with nothing attached, the add buttons sit among the actions instead of two empty headings */}
      {pagesHere.length > 0 && <PagesSection parentId={task.id} ws={task.labels.ws} />}
      {listsHere.length > 0 && <ListsSection parentId={task.id} ws={task.labels.ws} />}
      <TaskRuns taskId={task.id} />
      {converting && <ConvertSheet source={task} tasks={tasks} onClose={() => setConverting(false)} />}
      {handOff && <HandToAgentSheet task={task} project={project} onClose={() => setHandOff(false)} />}

      <ActionBar className="mb-6" items={actions} label="Task actions" testid="task-actions" />

      {(task.status.sessions?.length ?? 0) > 0 && (
        <details className="text-xs text-faint">
          <summary className="cursor-pointer min-h-8 flex items-center">Sessions</summary>
          <ul className="mt-1 space-y-0.5">
            {task.status.sessions!.map((s, i) => (
              <li key={i}>
                {new Date(s.start).toLocaleString()} → {new Date(s.end).toLocaleTimeString()}
              </li>
            ))}
          </ul>
        </details>
      )}

      {editing && <TaskForm resource={task} onDone={() => setEditing(false)} />}
      {defer && (
        <DeferMenu task={task} open onClose={() => setDefer(false)} defaultDate={task.spec.scheduled} />
      )}
      {pickProject && (
        <ProjectPicker task={task} open onClose={() => setPickProject(false)} current={projectId} />
      )}
      {pickPeople && (
        <PeoplePicker
          title={task.status.state === 'waiting' ? 'Waiting on' : 'With'}
          chosen={withPeople.map((p) => p.id)}
          onChange={(ids) =>
            void mutate.grouped(async () => {
              const had = new Set(withPeople.map((p) => p.id))
              for (const id of ids) if (!had.has(id)) await mutate.setEdge(task.id, id, 'references')
              for (const id of had)
                if (!ids.includes(id)) await mutate.setEdge(task.id, id, 'references', true)
            })
          }
          onClose={() => setPickPeople(false)}
        />
      )}
      <span className="hidden">{String(flash)}</span>
    </article>
  )
}

// ---- form -----------------------------------------------------------------

// HTML inputs yield '' for an empty date/time; accept that in the form and drop it on save
const orEmpty = <T extends z.ZodTypeAny>(inner: T) => z.union([z.literal(''), inner]).optional()
const formSchema = taskSpec
  .omit({ checklist: true, due: true, scheduled: true, scheduled_time: true })
  .extend({
    due: orEmpty(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use YYYY-MM-DD')),
    scheduled: orEmpty(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use YYYY-MM-DD')),
    scheduled_time: orEmpty(z.string().regex(/^[0-2][0-9]:[0-5][0-9]$/, 'use HH:MM')),
    labels: z.record(z.string(), z.string()).optional(),
    checklistText: z.string().optional(),
    projectId: z.string().optional(),
  })
type FormValues = z.output<typeof formSchema>
type FormInput = z.input<typeof formSchema>

/** How often a house job comes round (spec.every_days); 3 is where a new job starts. */
const JOB_OPTIONS = [
  { value: 0, label: 'Not a job' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 7, label: 'Weekly' },
  { value: 14, label: 'Fortnightly' },
  { value: 30, label: 'Monthly' },
]

const NAG_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 h' },
]

/** A titled group inside the task form: a hairline and a small heading, nothing boxed. */
const FormSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset className="border-t border-line pt-3 mt-1 mb-2 min-w-0">
    <legend className="pr-2 text-xs font-medium text-fg">{title}</legend>
    {children}
  </fieldset>
)

/** Full task form for create and edit. Empty optional fields are dropped. */
/** The form's day, time and estimate, read as they are typed, for the clash note. */
const FormClash = ({ control }: { control: Control<FormInput, unknown, FormValues> }) => {
  const [day, time, est] = useWatch({ control, name: ['scheduled', 'scheduled_time', 'estimate_minutes'] })
  return <ClashNote day={day || undefined} time={time || undefined} minutes={Number(est) || 0} />
}

/** Under "Remind me at the scheduled time": whose phone it rings on too, once it has a day and a time. */
const FormRings = ({
  control,
  taskId,
  ws,
}: {
  control: Control<FormInput, unknown, FormValues>
  taskId: string
  ws?: string
}) => {
  const [remind, day, time] = useWatch({ control, name: ['remind', 'scheduled', 'scheduled_time'] })
  if (!remind || !day || !time) return null
  return <RingsToo taskId={taskId} ws={ws} className="-mt-2 mb-3" />
}

export const TaskForm = ({
  resource,
  onDone,
  initialText,
  initialProjectId,
}: {
  resource?: Resource<TaskSpec, TaskStatus>
  onDone: () => void
  /** quick-add text; its syntax tokens prefill the fields */
  initialText?: string
  initialProjectId?: string
}) => {
  const projects = (useProjects() ?? []).filter(
    (p) => p.status.state !== 'done' && p.status.state !== 'dropped',
  )
  // "Advanced" from quick-add: keep what the shorthand already said
  const parsed = !resource && initialText ? quickAddParse(initialText) : undefined
  const parsedProject = parsed?.project ? matchProject(parsed.project, projects)?.id : undefined
  const edges = useEdgesFrom(resource?.id)
  const currentProject = edges.find((e) => e.relation === 'belongs_to')?.to_id ?? ''
  // the project's "Keep posted" covers the task, and the switch says so
  const parent = useAllProjects()?.find((p) => p.id === (currentProject || undefined))
  const [error, setError] = useState<string | null>(null)
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: resource
      ? {
          ...resource.spec,
          // `ws` is the workspace, `via` which share link a capture came through, `posted.*` who is kept posted: kept, never shown or edited here
          labels: Object.fromEntries(Object.entries(resource.labels).filter(([k]) => !hiddenLabel(k))),
          checklistText: (resource.spec.checklist ?? [])
            .map((c) => (c.done ? `[x] ${c.text}` : c.text))
            .join('\n'),
          projectId: currentProject,
        }
      : {
          title: parsed?.spec.title ?? initialText ?? '',
          remind: false,
          priority: parsed?.spec.priority ?? 0,
          scheduled: parsed?.spec.scheduled,
          due: parsed?.spec.due,
          estimate_minutes: parsed?.spec.estimate_minutes,
          labels: { ...(parsed?.labels ?? {}) },
          checklistText: '',
          projectId: parsedProject ?? initialProjectId ?? '',
        },
  })
  const { register, handleSubmit, formState, control } = form
  // The task's links load after the first render, so the project reads "none"
  // until they arrive: the field follows them until the owner picks one, and a
  // save changes the project only when the field was changed (saving that
  // stale "none" took tasks out of their projects; the UI audit, 10 Oct)
  useEffect(() => {
    if (resource && !form.getFieldState('projectId').isDirty)
      form.resetField('projectId', { defaultValue: currentProject })
  }, [resource, currentProject, form])

  const submit = async (v: FormValues) => {
    setError(null)
    const checklist = (v.checklistText ?? '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) =>
        /^\[x\]\s*/i.test(l)
          ? { text: l.replace(/^\[x\]\s*/i, ''), done: true }
          : { text: l.replace(/^\[\s?\]\s*/, ''), done: false },
      )
    const spec: Record<string, unknown> = {
      title: v.title.trim(),
      icon: v.icon || undefined,
      remind: v.remind,
      priority: v.priority,
      notes: v.notes?.trim() || undefined,
      due: v.due || undefined,
      scheduled: v.scheduled || undefined,
      scheduled_time: v.scheduled_time || undefined,
      estimate_minutes: v.estimate_minutes || undefined,
      recurrence: v.recurrence?.trim() || undefined,
      nag_minutes: v.nag_minutes || undefined,
      every_days: v.every_days || undefined,
      checklist: checklist.length ? checklist : undefined,
    }
    const labels = v.labels ?? {}
    try {
      if (resource) {
        const patchSpec: Record<string, unknown | null> = {}
        for (const key of Object.keys(spec)) {
          const before = (resource.spec as Record<string, unknown>)[key]
          const after = spec[key]
          if (JSON.stringify(before) !== JSON.stringify(after))
            patchSpec[key] = after === undefined ? null : after
        }
        const patchLabels: Record<string, string | null> = {}
        for (const k of new Set([...Object.keys(resource.labels), ...Object.keys(labels)])) {
          if (!hiddenLabel(k) && resource.labels[k] !== labels[k]) patchLabels[k] = labels[k] ?? null
        }
        if (Object.keys(patchSpec).length || Object.keys(patchLabels).length)
          await mutate.patch(resource.id, {
            ...(Object.keys(patchSpec).length ? { spec: patchSpec } : {}),
            ...(Object.keys(patchLabels).length ? { labels: patchLabels } : {}),
          })
        if (form.getFieldState('projectId').isDirty && (v.projectId ?? '') !== currentProject)
          await mutate.setProject(resource.id, v.projectId || null)
      } else {
        const clean = Object.fromEntries(Object.entries(spec).filter(([, val]) => val !== undefined))
        const r = await mutate.create('Task', clean, labels)
        if (v.projectId) await mutate.setProject(r.id, v.projectId)
      }
      onDone()
    } catch (e) {
      setError(e instanceof KindError ? e.message : (e as Error).message)
    }
  }
  const err = (k: keyof FormInput) => formState.errors[k]?.message as string | undefined
  const numOpt = { setValueAs: (v: string) => (v === '' || v === null ? undefined : Number(v)) }
  return (
    <Sheet
      open
      onClose={onDone}
      title={resource ? 'Edit task' : 'New task'}
      footer={
        <>
          <Button onClick={onDone}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="task-form"
            disabled={formState.isSubmitting}
            data-testid="form-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form id="task-form" onSubmit={handleSubmit(submit)} noValidate>
        <div className="flex gap-3 items-start">
          <Controller
            control={control}
            name="icon"
            render={({ field }) => (
              <IconButtonField value={field.value ?? undefined} onChange={field.onChange} />
            )}
          />
          <div className="flex-1">
            <Field label="Title" error={err('title')}>
              <input
                {...register('title')}
                className="w-full"
                autoFocus={!resource}
                data-testid="form-title"
              />
            </Field>
          </div>
        </div>
        <Field label="Notes">
          <textarea {...register('notes')} rows={2} className="w-full" />
        </Field>
        {resource && (
          <KeepPosted
            id={resource.id}
            labels={resource.labels}
            parentLabels={parent?.labels}
            parentTitle={parent?.spec.title}
          />
        )}
        <FormSection title="When">
          <div className="grid grid-cols-2 gap-x-3">
            <Field label="Scheduled" error={err('scheduled')}>
              <input type="date" {...register('scheduled')} className="w-full" />
            </Field>
            <Field label="Time" error={err('scheduled_time')}>
              <input type="time" {...register('scheduled_time')} className="w-full" />
            </Field>
            <div className="col-span-2">
              <FormClash control={form.control} />
            </div>
            <Field label="Due" error={err('due')}>
              <input type="date" {...register('due')} className="w-full" />
            </Field>
            <Field label="Estimate (min)" error={err('estimate_minutes')}>
              <input
                type="number"
                min={1}
                inputMode="numeric"
                {...register('estimate_minutes', numOpt)}
                className="w-full"
              />
            </Field>
          </div>
        </FormSection>
        <FormSection title="Organise">
          <div className="grid grid-cols-2 gap-x-3">
            <Field label="Priority">
              <select {...register('priority', { valueAsNumber: true })} className="w-full">
                {PRIORITY_WORD.map((w, i) => (
                  <option key={w} value={i}>
                    {i ? `${'!'.repeat(i)} ${w}` : w}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Project">
              <select {...register('projectId')} className="w-full">
                <option value="">No project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.spec.title}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Labels">
            <Controller
              control={control}
              name="labels"
              render={({ field }) => (
                <LabelsEditor
                  entries={Object.entries(field.value ?? {})}
                  onAdd={(k, val) => field.onChange({ ...(field.value ?? {}), [k]: val })}
                  onRemove={(k) =>
                    field.onChange(
                      Object.fromEntries(Object.entries(field.value ?? {}).filter(([x]) => x !== k)),
                    )
                  }
                  testid="form-labels"
                />
              )}
            />
          </Field>
        </FormSection>
        <FormSection title="Repeat & remind">
          <Field label="Repeat" error={err('recurrence')}>
            <Controller
              control={control}
              name="recurrence"
              render={({ field }) => (
                <RepeatPicker value={field.value ?? undefined} onChange={field.onChange} />
              )}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" {...register('remind')} /> Remind me at the scheduled time
          </label>
          {resource && <FormRings control={form.control} taskId={resource.id} ws={resource.labels.ws} />}
          <Field
            label="Nag until done"
            hint="Meds-style: keeps reminding at the scheduled time until it's done. Needs a time and a repeat."
            error={err('nag_minutes')}
          >
            <Controller
              control={control}
              name="nag_minutes"
              render={({ field }) => (
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Nag every">
                  {NAG_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={(field.value ?? 0) === o.value}
                      onClick={() => field.onChange(o.value || undefined)}
                      className={cx(
                        'rounded-full px-3 min-h-8 text-xs font-medium transition-colors border',
                        (field.value ?? 0) === o.value
                          ? 'bg-accent text-on-accent border-accent'
                          : 'bg-raised text-muted border-line hover:text-fg',
                      )}
                      data-testid={`form-nag-${o.value}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </Field>
        </FormSection>
        <FormSection title="House job">
          <Field
            label="Comes round every"
            hint="Done now and then rather than on a day: one gentle nudge when it is due, and Done for now starts it again."
            error={err('every_days')}
          >
            <Controller
              control={control}
              name="every_days"
              render={({ field }) => (
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Comes round every">
                  {JOB_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={(field.value ?? 0) === o.value}
                      onClick={() => field.onChange(o.value || undefined)}
                      className={cx(
                        'rounded-full px-3 min-h-8 text-xs font-medium transition-colors border',
                        (field.value ?? 0) === o.value
                          ? 'bg-accent text-on-accent border-accent'
                          : 'bg-raised text-muted border-line hover:text-fg',
                      )}
                      data-testid={`form-job-${o.value}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </Field>
        </FormSection>
        <FormSection title="Checklist">
          <Field label="Steps" hint="One per line. Prefix [x] for done.">
            <textarea {...register('checklistText')} rows={3} className="w-full font-mono text-sm" />
          </Field>
        </FormSection>
        {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
      </form>
    </Sheet>
  )
}
